/**
 * BaseModal Component
 *
 * Reusable modal wrapper with consistent structure:
 * - Backdrop with click-to-close
 * - Header with title and close button
 * - Content area (children)
 * - Optional footer with action buttons
 * - Escape key handling
 */

import React, { useEffect, useId } from 'react';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: string; // FontAwesome icon class (e.g., "fa-solid fa-user")
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '70p' | '80p'; // 70p = 70% of screen width
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  preventClose?: boolean; // Disable close button and escape key (e.g., during save)
  nested?: boolean; // If true, uses higher z-index for nested modals (z-60 instead of z-50)
  doublyNested?: boolean; // If true, uses even higher z-index for doubly nested modals (z-70 instead of z-50/60)
  fullScreenOnMobile?: boolean; // If true, modal takes full screen on mobile (< 768px)
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '70p': 'w-[95%] sm:max-w-[85%] lg:max-w-[70%]',
  '80p': 'w-[95%] sm:max-w-[90%] lg:max-w-[80%]',
};

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  maxWidth = '2xl',
  closeOnBackdrop = true,
  closeOnEscape = true,
  preventClose = false,
  nested = false,
  doublyNested = false,
  fullScreenOnMobile = false,
}) => {
  // Unique per instance so a nested modal is named by its own title
  const titleId = useId();

  // Handle Escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape || preventClose) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, preventClose, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (closeOnBackdrop && !preventClose) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    if (!preventClose) {
      onClose();
    }
  };

  // Determine z-index level
  const zIndex = doublyNested ? 'z-70' : nested ? 'z-60' : 'z-50';

  // Build modal container classes based on fullScreenOnMobile setting
  // When fullScreenOnMobile is true:
  // - On mobile (< md): full screen, no rounded corners, full height
  // - On desktop (>= md): normal centered modal with max-width
  const mobileFullScreenClasses = fullScreenOnMobile
    ? 'max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:rounded-none md:rounded-xl md:max-h-[90vh]'
    : 'rounded-xl max-h-[90vh]';

  const modalContainerClasses = `relative bg-surface shadow-2xl ${maxWidthClasses[maxWidth]} w-full overflow-hidden flex flex-col animate-scale-in ${mobileFullScreenClasses}`;

  // Wrapper padding is removed on mobile for full-screen mode
  const wrapperClasses = fullScreenOnMobile
    ? `fixed inset-0 ${zIndex} flex items-center justify-center md:p-4 animate-fade-in`
    : `fixed inset-0 ${zIndex} flex items-center justify-center p-4 animate-fade-in`;

  return (
    <div className={wrapperClasses}>
      {/* Backdrop - hidden on mobile for full-screen mode */}
      <div
        className={`absolute inset-0 bg-scrim/50 backdrop-blur-sm ${fullScreenOnMobile ? 'max-md:hidden' : ''}`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={modalContainerClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Header */}
        <div className="sticky top-0 bg-surface border-b border-line px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div className="flex-1">
            <h2 id={titleId} className="text-xl font-bold text-ink flex items-center">
              {icon && <i className={`${icon} mr-2`} aria-hidden="true"></i>}
              {title}
            </h2>
            {subtitle && <p className="text-sm text-ink-body mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={handleCloseClick}
            disabled={preventClose}
            className="text-ink-faint hover:text-ink-body disabled:opacity-50 disabled:cursor-not-allowed ml-4"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="sticky bottom-0 bg-surface border-t border-line px-6 py-4 rounded-b-xl z-10">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Standard Footer Buttons
 *
 * Common Cancel/Save button pattern for forms
 */
export interface BaseModalFooterProps {
  onCancel: () => void;
  onSubmit?: () => void;
  cancelText?: string;
  submitText?: string;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  /** 'danger' for a destructive submit (delete, cancel a record) */
  variant?: 'primary' | 'danger';
}

// danger-deep re-points light in dark mode, under white ink, so the danger hover dims instead
const SUBMIT_FILL = {
  primary: 'bg-action hover:bg-action-hover',
  danger: 'bg-danger hover:opacity-90',
};

export const BaseModalFooter: React.FC<BaseModalFooterProps> = ({
  onCancel,
  onSubmit,
  cancelText = 'Cancel',
  submitText = 'Save',
  isSubmitting = false,
  submitDisabled = false,
  variant = 'primary',
}) => {
  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={isSubmitting}
        className="flex-1 px-4 py-2 border border-line-strong rounded-lg text-ink-body hover:bg-surface-muted font-medium transition-colors disabled:opacity-50"
      >
        {cancelText}
      </button>
      {onSubmit && (
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting || submitDisabled}
          className={`flex-1 px-4 py-2 ${SUBMIT_FILL[variant]} text-ink-inverse rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? 'Saving...' : submitText}
        </button>
      )}
    </div>
  );
};
